import type {
  EgressLinkListEntry,
  EgressNodeTrafficRow,
  EgressPathSnapshotRow,
  EgressPathSwitchEventRecord,
  EgressPathTargetObservation,
} from '@nadoutong/contracts';

export const SNAPSHOT_STALE_AFTER_MS = 5 * 60 * 1000;
/** Longer than a rolling deploy; live instances keep posting snapshots ~60s. */
export const NODE_ACTIVE_AFTER_MS = 15 * 60 * 1000;

export type LineBadge =
  | 'excellent'
  | 'good'
  | 'degraded'
  | 'breaker'
  | 'same_region'
  | 'empty'
  | 'no_traffic'
  | 'stale_unknown';

export interface MatrixCell {
  readonly pathId: string;
  readonly sortKeyMs: number | null;
  readonly badge: LineBadge;
  readonly capturedAt: string | null;
}

export interface MatrixNode {
  readonly nodeId: string;
  readonly gatewayRegion: string;
  readonly capturedAt: string | null;
  readonly lastActivityAt: string | null;
  readonly stale: boolean;
  readonly active: boolean;
  readonly ageSeconds: number | null;
  readonly cells: Readonly<Record<string, MatrixCell>>;
  readonly paths: readonly EgressPathSnapshotRow[];
  readonly traffic: EgressNodeTrafficRow | null;
}

export function destRegionsFor(
  snapshots: readonly EgressPathSnapshotRow[],
  links: readonly EgressLinkListEntry[],
  events: readonly EgressPathSwitchEventRecord[],
  serving: readonly EgressNodeTrafficRow[] = [],
): readonly string[] {
  const dests = new Set<string>();
  for (const row of snapshots) {
    dests.add(row.gateway_region);
    for (const target of row.target_observations) {
      if (typeof target.upstream_region === 'string' && target.upstream_region.length > 0) {
        dests.add(target.upstream_region);
      }
    }
  }
  for (const link of links) dests.add(link.region);
  for (const event of events) dests.add(event.dest_region);
  for (const node of serving) dests.add(node.gateway_region);
  return [...dests].sort();
}

export function buildMatrix(
  snapshots: readonly EgressPathSnapshotRow[],
  events: readonly EgressPathSwitchEventRecord[],
  nowMs: number,
  serving: readonly EgressNodeTrafficRow[] = [],
  links: readonly EgressLinkListEntry[] = [],
): readonly MatrixNode[] {
  const byNode = new Map<string, EgressPathSnapshotRow[]>();
  for (const row of snapshots) {
    const existing = byNode.get(row.node_id) ?? [];
    byNode.set(row.node_id, [...existing, row]);
  }
  const servingByNode = new Map(serving.map((row) => [row.node_id, row]));
  const nodeIds = [...new Set([...byNode.keys(), ...servingByNode.keys()])].sort((a, b) => a.localeCompare(b));
  const dests = destRegionsFor(snapshots, links, events, serving);
  return nodeIds.map((nodeId) => {
    const paths = byNode.get(nodeId) ?? [];
    const traffic = servingByNode.get(nodeId) ?? null;
    const capturedAt = newestCapturedAt(paths);
    const lastActivityAt = newestTimestamp(capturedAt, traffic?.last_seen_at ?? null);
    const activityMs = lastActivityAt === null ? null : Date.parse(lastActivityAt);
    const ageMs = capturedAt === null ? null : nowMs - Date.parse(capturedAt);
    const stale = paths.length > 0
      && ageMs !== null
      && Number.isFinite(ageMs)
      && ageMs > SNAPSHOT_STALE_AFTER_MS;
    const active = activityMs !== null
      && Number.isFinite(activityMs)
      && nowMs - activityMs <= NODE_ACTIVE_AFTER_MS;
    const gatewayRegion = paths[0]?.gateway_region
      ?? traffic?.gateway_region
      ?? 'jp';
    const cells: Record<string, MatrixCell> = {};
    for (const dest of dests) {
      cells[dest] = cellFor(dest, gatewayRegion, paths, stale, capturedAt);
    }
    return {
      nodeId,
      gatewayRegion,
      capturedAt,
      lastActivityAt,
      stale,
      active,
      ageSeconds: ageMs === null || !Number.isFinite(ageMs) ? null : Math.max(0, Math.floor(ageMs / 1000)),
      cells,
      paths,
      traffic,
    };
  });
}

export function partitionMatrixNodes(
  nodes: readonly MatrixNode[],
): { readonly active: readonly MatrixNode[]; readonly historical: readonly MatrixNode[] } {
  return {
    active: nodes.filter((node) => node.active),
    historical: nodes.filter((node) => !node.active),
  };
}

function newestTimestamp(left: string | null, right: string | null): string | null {
  if (left === null) return right;
  if (right === null) return left;
  return left >= right ? left : right;
}

function newestCapturedAt(paths: readonly EgressPathSnapshotRow[]): string | null {
  let newest: string | null = null;
  for (const path of paths) {
    if (newest === null || path.captured_at > newest) newest = path.captured_at;
  }
  return newest;
}

function cellFor(
  dest: string,
  gatewayRegion: string,
  paths: readonly EgressPathSnapshotRow[],
  stale: boolean,
  capturedAt: string | null,
): MatrixCell {
  if (dest === gatewayRegion) {
    return {
      pathId: 'direct',
      sortKeyMs: null,
      badge: 'same_region',
      capturedAt,
    };
  }
  const observations = observationsForDest(paths, dest);
  if (observations.length === 0) {
    return {
      pathId: '—',
      sortKeyMs: null,
      badge: 'no_traffic',
      capturedAt,
    };
  }
  if (stale || paths.length === 0) {
    return {
      pathId: '—',
      sortKeyMs: null,
      badge: 'stale_unknown',
      capturedAt,
    };
  }
  const chosen = chosenObservation(observations);
  const snapshot = paths.find((path) => path.path_id === chosen.pathId) ?? null;
  const badge = snapshot === null
    ? 'empty'
    : !snapshot.qualified
      ? 'degraded'
      : snapshot.p75_sort_key_ms !== null && snapshot.p75_sort_key_ms < 80
        ? 'excellent'
        : 'good';
  return {
    pathId: chosen.pathId,
    sortKeyMs: snapshot?.p75_sort_key_ms ?? chosen.sortKeyMs,
    badge,
    capturedAt: snapshot?.captured_at ?? capturedAt,
  };
}

function observationsForDest(
  paths: readonly EgressPathSnapshotRow[],
  dest: string,
): readonly { readonly pathId: string; readonly target: EgressPathTargetObservation; readonly sortKeyMs: number | null }[] {
  const rows = [];
  for (const path of paths) {
    for (const target of path.target_observations) {
      if (target.upstream_region !== dest) continue;
      rows.push({
        pathId: target.decision?.selected_path_id ?? path.path_id,
        target,
        sortKeyMs: target.p75_sort_key_ms,
      });
    }
  }
  return rows;
}

function chosenObservation(
  observations: readonly { readonly pathId: string; readonly target: EgressPathTargetObservation; readonly sortKeyMs: number | null }[],
): { readonly pathId: string; readonly sortKeyMs: number | null } {
  const selected = observations.filter((row) => row.target.selected);
  const pool = selected.length > 0 ? selected : observations;
  const first = pool[0];
  if (first === undefined) return { pathId: '—', sortKeyMs: null };
  let best = first;
  for (const row of pool) {
    if (row.target.sample_count > best.target.sample_count) best = row;
  }
  return { pathId: best.pathId, sortKeyMs: best.sortKeyMs };
}
